// Operator profile page — edit name/phone and change password.
//
// Email is intentionally read-only (it's the login credential + welcome
// link target). Email change requires a separate verify-new-email flow
// that we haven't built yet.

import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

type Tab = 'info' | 'password';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  foreman: 'Foreman',
  collector: 'Collector',
  read_only: 'Read only',
};

export function ProfilePage(): ReactElement {
  const qc = useQueryClient();
  const [tab, set_tab] = useState<Tab>('info');

  const me_q = useQuery({
    queryKey: ['me'],
    queryFn: () => orpc.me.get({}),
  });

  if (me_q.isLoading) {
    return <div className="text-sm text-ink/60">Loading…</div>;
  }
  if (me_q.error || !me_q.data) {
    return (
      <div className="text-sm text-bad">
        Failed to load profile: {(me_q.error as Error | undefined)?.message ?? 'unknown error'}
      </div>
    );
  }
  const p = me_q.data.profile;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-medium">Profile</h1>
        <p className="text-sm text-ink/60 mt-1">
          {p.name} · <span className="font-mono">{p.email ?? '—'}</span> ·{' '}
          {ROLE_LABEL[p.role] ?? p.role}
        </p>
      </div>

      <div className="flex gap-1 mb-6 border-b border-line">
        {(['info', 'password'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => set_tab(t)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === t
                ? 'border-primary text-primary'
                : 'border-transparent text-ink/60 hover:text-ink'
            }`}
          >
            {t === 'info' ? 'Profile info' : 'Password'}
          </button>
        ))}
      </div>

      {tab === 'info' && <InfoTab initial_name={p.name} initial_phone={p.phone} email={p.email} on_saved={() => qc.invalidateQueries({ queryKey: ['me'] })} />}
      {tab === 'password' && <PasswordTab />}
    </div>
  );
}

// ─── Profile info tab ─────────────────────────────────────────────────────

function InfoTab({
  initial_name,
  initial_phone,
  email,
  on_saved,
}: {
  initial_name: string;
  initial_phone: string | null;
  email: string | null;
  on_saved: () => void;
}): ReactElement {
  const [name, set_name] = useState(initial_name);
  const [phone, set_phone] = useState(initial_phone ?? '');
  const [success, set_success] = useState(false);

  // Reset success banner when the form is edited again.
  useEffect(() => { set_success(false); }, [name, phone]);

  const save = useMutation({
    mutationFn: (args: { name?: string; phone?: string | null }) =>
      orpc.me.update_profile(args),
    onSuccess: () => {
      set_success(true);
      on_saved();
    },
  });

  const dirty = name !== initial_name || (phone || null) !== (initial_phone || null);

  function submit(e: FormEvent): void {
    e.preventDefault();
    const args: { name?: string; phone?: string | null } = {};
    if (name !== initial_name) args.name = name;
    if ((phone || null) !== (initial_phone || null)) {
      args.phone = phone.trim() === '' ? null : phone.trim();
    }
    save.mutate(args);
  }

  return (
    <form onSubmit={submit}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => set_name(e.target.value)}
            required
            minLength={1}
            maxLength={120}
            className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
            Email <span className="text-ink/40">(read-only)</span>
          </label>
          <input
            type="email"
            value={email ?? ''}
            disabled
            className="w-full px-3 py-2 bg-bg-elev border border-line rounded text-sm text-ink/60 font-mono"
          />
          <p className="text-[11px] text-ink/50 mt-1">
            Login credential. Contact support to change.
          </p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
            Phone
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => set_phone(e.target.value)}
            placeholder="+919876543210"
            pattern="^\+91[6-9]\d{9}$"
            className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-mono"
          />
          <p className="text-[11px] text-ink/50 mt-1">+91 mobile. Blank to clear.</p>
        </div>
      </div>

      {save.error && (
        <div className="text-sm text-bad bg-bad-soft px-3 py-2 rounded mt-4">
          {friendly_error(save.error)}
        </div>
      )}
      {success && !dirty && (
        <div className="text-sm text-good bg-good-soft px-3 py-2 rounded mt-4">
          Saved.
        </div>
      )}

      <button
        type="submit"
        disabled={!dirty || save.isPending}
        className="mt-6 px-4 py-2 bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white rounded text-sm"
      >
        {save.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}

// ─── Password tab ─────────────────────────────────────────────────────────

function PasswordTab(): ReactElement {
  const [current, set_current] = useState('');
  const [next, set_next] = useState('');
  const [confirm, set_confirm] = useState('');
  const [success, set_success] = useState(false);

  const change = useMutation({
    mutationFn: (args: { current_password: string; new_password: string }) =>
      orpc.me.change_password(args),
    onSuccess: () => {
      set_success(true);
      set_current('');
      set_next('');
      set_confirm('');
    },
  });

  const valid =
    current.length >= 8 &&
    next.length >= 8 &&
    next === confirm &&
    /\d/.test(next) &&
    /[a-zA-Z]/.test(next);

  function submit(e: FormEvent): void {
    e.preventDefault();
    set_success(false);
    change.mutate({ current_password: current, new_password: next });
  }

  return (
    <form onSubmit={submit}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
            Current password
          </label>
          <input
            type="password"
            value={current}
            onChange={(e) => set_current(e.target.value)}
            required
            minLength={8}
            autoComplete="current-password"
            className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
            New password
          </label>
          <input
            type="password"
            value={next}
            onChange={(e) => set_next(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
          />
          <p className="text-[11px] text-ink/50 mt-1">≥ 8 chars; letters + digits.</p>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
            Confirm new password
          </label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => set_confirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
          />
          {confirm.length > 0 && next !== confirm && (
            <p className="text-[11px] text-bad mt-1">Passwords don't match.</p>
          )}
        </div>
      </div>

      {change.error && (
        <div className="text-sm text-bad bg-bad-soft px-3 py-2 rounded mt-4">
          {friendly_error(change.error)}
        </div>
      )}
      {success && (
        <div className="text-sm text-good bg-good-soft px-3 py-2 rounded mt-4">
          Password updated.
        </div>
      )}

      <button
        type="submit"
        disabled={!valid || change.isPending}
        className="mt-6 px-4 py-2 bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white rounded text-sm"
      >
        {change.isPending ? 'Updating…' : 'Update password'}
      </button>
    </form>
  );
}
